import { Briefcase, Users, DollarSign, Brain, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNavigationStore } from '../../stores/navigationStore';

interface Tab {
  id: 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin';
  name: string;
  shortName: string;
  icon: React.ElementType;
  description: string;
  activeColor: string;
}

const tabs: Tab[] = [
  {
    id: 'operations',
    name: 'Operations',
    shortName: 'Ops',
    icon: Briefcase,
    description: 'Bookings, PNRs, trips, and day-to-day workflows',
    activeColor: 'text-blue-700 bg-blue-50 border-blue-200',
  },
  {
    id: 'crm',
    name: 'CRM',
    shortName: 'CRM',
    icon: Users,
    description: 'Customers, companies, travelers, and communications',
    activeColor: 'text-purple-700 bg-purple-50 border-purple-200',
  },
  {
    id: 'finance',
    name: 'Finance',
    shortName: 'Fin',
    icon: DollarSign,
    description: 'Invoices, payments, banking, and accounting',
    activeColor: 'text-green-700 bg-green-50 border-green-200',
  },
  {
    id: 'intelligence',
    name: 'Intelligence',
    shortName: 'Intel',
    icon: Brain,
    description: 'AI tools, analytics, reports, and risk monitoring',
    activeColor: 'text-amber-700 bg-amber-50 border-amber-200',
  },
  {
    id: 'admin',
    name: 'Admin',
    shortName: 'Admin',
    icon: Settings,
    description: 'Users, settings, integrations, and configuration',
    activeColor: 'text-slate-700 bg-slate-50 border-slate-200',
  },
];

interface TabbedNavigationProps {
  allowedTabIds?: ('operations' | 'crm' | 'finance' | 'intelligence' | 'admin')[];
  tabTargets: ReadonlyMap<Tab['id'], string>;
}

export function TabbedNavigation({ allowedTabIds, tabTargets }: TabbedNavigationProps) {
  const navigate = useNavigate();
  const { activeTab, setActiveTab, density } = useNavigationStore();

  const handleTabChange = (tabId: Tab['id']) => {
    const target = tabTargets.get(tabId);
    if (!target) return;
    setActiveTab(tabId);
    navigate(target);
  };

  const filteredTabs = allowedTabIds
    ? tabs.filter(tab => allowedTabIds.includes(tab.id) && tabTargets.has(tab.id))
    : tabs.filter(tab => tabTargets.has(tab.id));

  const paddingClass = {
    compact: 'py-1 px-1',
    normal: 'py-1 px-1',
    comfortable: 'py-1.5 px-1',
  }[density];

  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
  }[filteredTabs.length] || 'grid-cols-5';

  return (
    <div className="border-b border-gray-200 bg-gray-50 overflow-hidden">
      <nav className={`grid ${gridColsClass} gap-0.5 px-1.5 pt-1.5`} aria-label="Navigation domains">
        {filteredTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              type="button"
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex w-full flex-col items-center justify-center rounded-t-lg ${paddingClass} text-[10px] font-medium transition-all focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:text-xs ${
                isActive
                  ? `${tab.activeColor} border-t border-x shadow-sm`
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title={tab.description}
              aria-label={tab.name}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className={`w-4 h-4 ${isActive ? '' : 'opacity-70'}`} />
              <span className="truncate mt-0.5">{tab.shortName}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
